import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Forum as ForumIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { surface } from '../theme';
import AuthService from '../services/AuthService';
import VideoIdeaService from '../services/VideoIdeaService';
import ApiKeyService from '../services/ApiKeyService';
import { getPromptSystems } from '../services/StoryScriptService';
import { formatEastern } from '../utils/easternTime';

const PAGE_SIZE = 24;
const TABS = [
  { status: 'queued', label: 'Backlog' },
  { status: 'used', label: 'Used' },
];

function IdeaThumbnail({ idea }) {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl = '';
    let active = true;
    VideoIdeaService.thumbnail(idea.id)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => { if (active) setFailed(true); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [idea.id]);

  return (
    <Box sx={{ position: 'relative', aspectRatio: '16 / 9', bgcolor: '#eceef6', borderRadius: '10px', overflow: 'hidden' }}>
      {url ? (
        <Box component="img" src={url} alt={idea.title} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : failed ? (
        <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          Thumbnail unavailable
        </Typography>
      ) : (
        <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%' }} />
      )}
    </Box>
  );
}

function IdeaCard({ idea, onDelete }) {
  return (
    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
      <IdeaThumbnail idea={idea} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>{idea.title}</Typography>
      {idea.context && (
        <Tooltip title={<Box sx={{ whiteSpace: 'pre-wrap' }}>{idea.context}</Box>}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {idea.context}
          </Typography>
        </Tooltip>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 'auto' }}>
        <Chip size="small" label={idea.niche} />
        {idea.status === 'queued' && (
          <Chip size="small" variant="outlined" label={idea.channel ? `Only ${idea.channel.name}` : 'Any channel'} />
        )}
        {idea.source && <Chip size="small" variant="outlined" label={idea.source} />}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 34 }}>
        {idea.status === 'used' ? (
          <Typography variant="caption" color="text.secondary">
            {idea.usedChannel?.name || 'Channel'}
            {idea.scheduledPublishAt && ` · ${formatEastern(idea.scheduledPublishAt)}`}
            {idea.storyId && (
              <>
                {' · '}
                <Link component={RouterLink} to={`/ai-stories/${idea.storyId}`}>Open story</Link>
              </>
            )}
          </Typography>
        ) : idea.status === 'claimed' ? (
          <Typography variant="caption" color="text.secondary">Creating a video…</Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Added {new Date(idea.createdAt).toLocaleDateString()}
          </Typography>
        )}
        {idea.status === 'queued' && (
          <IconButton size="small" color="error" aria-label={`Delete idea ${idea.title}`} onClick={() => onDelete(idea)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Paper>
  );
}

function ApiAccessPanel() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    try {
      setKeys(await ApiKeyService.list());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load API keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async (text, label) => {
    await navigator.clipboard.writeText(text);
    setCopied(label);
  };

  const create = async () => {
    try {
      setBusy(true);
      setError('');
      setCreatedKey(await ApiKeyService.create(newKeyName.trim()));
      setCreating(false);
      setNewKeyName('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the API key.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (key) => {
    try {
      await ApiKeyService.revoke(key.id);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not revoke the API key.');
    }
  };

  const endpoint = VideoIdeaService.apiUrl();
  const example = [
    `curl -X POST ${endpoint} \\`,
    '  -H "Authorization: Bearer $VID_HELPER_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '{"ideas":[{"niche":"bikers","title":"…","context":"…",` +
      '"thumbnail":{"data":"<base64>","contentType":"image/jpeg","fileName":"thumb.jpg"}}]}\'',
  ].join('\n');

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyIcon fontSize="small" /> API access
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>New key</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Ideation agents add ideas by POSTing up to 50 at a time to this endpoint with an API key.
        A key acts as you, with your role.
      </Typography>
      <Box sx={{ position: 'relative' }}>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            pr: 5,
            borderRadius: '10px',
            bgcolor: '#1d2030',
            color: '#e7e9f3',
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {example}
        </Box>
        <Tooltip title="Copy example">
          <IconButton
            size="small"
            onClick={() => copy(example, 'Example copied.')}
            sx={{ position: 'absolute', top: 6, right: 6, color: '#e7e9f3' }}
            aria-label="Copy example request"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {copied && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setCopied('')}>{copied}</Alert>}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>Your keys</Typography>
      {loading ? (
        <CircularProgress size={20} />
      ) : !keys.length ? (
        <Typography variant="body2" color="text.secondary">No keys yet.</Typography>
      ) : (
        keys.map((key) => (
          <Box key={key.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{key.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {key.prefix}… · {key.lastUsedAt ? `last used ${new Date(key.lastUsedAt).toLocaleString()}` : 'never used'}
              </Typography>
            </Box>
            <Button size="small" color="error" onClick={() => revoke(key)}>Revoke</Button>
          </Box>
        ))
      )}

      <Dialog open={creating} onClose={() => !busy && setCreating(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New API key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            label="Key name"
            placeholder="OpenCode ideation"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            disabled={busy}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreating(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={busy || !newKeyName.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(createdKey)} onClose={() => setCreatedKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy your new API key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time the key is shown. Store it as a secret, for example VID_HELPER_API_KEY.
          </Alert>
          <TextField
            fullWidth
            value={createdKey?.key || ''}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button startIcon={<ContentCopyIcon />} onClick={() => copy(createdKey.key, 'API key copied.')}>Copy</Button>
          <Button variant="contained" onClick={() => setCreatedKey(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

const RUN_STATUS = {
  launching: { label: 'Starting', color: 'info' },
  running: { label: 'Running', color: 'info' },
  succeeded: { label: 'Done', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
};
const ACTIVE_RUN_STATUSES = ['launching', 'running'];
const REFRESH_WHILE_RUNNING_MS = 30000;

// What today's daily runs are doing, or why the next one has not started.
function dailyDescription(ideation) {
  const today = ideation.dailyRunToday;
  const { totals, niche } = ideation;
  if (today) {
    if (ACTIVE_RUN_STATUSES.includes(today.status)) {
      return `Today's daily run ${today.runCount} is writing ideas and making their thumbnails.`;
    }
    if (today.status === 'failed') return "Today's last daily run failed. The next one is after midnight Eastern.";
    if (today.runCount >= ideation.maxDailyRuns) {
      return `All ${ideation.maxDailyRuns} of today's daily runs have finished. The next one is after midnight Eastern.`;
    }
  }
  if (!totals.dailyVideos) return `The daily run starts once an automated "${niche}" channel needs ideas.`;
  if (totals.ideasNeeded) {
    const count = Math.min(totals.ideasNeeded, ideation.maxIdeasPerRun);
    return `${today ? 'Another' : 'The'} daily run starts within a minute for ${count} ideas.`;
  }
  return `Backlogs already cover ${ideation.backlogDays} days. They are checked again throughout the day.`;
}

function runLabel(run) {
  if (run.kind === 'manual') return `${run.date} · requested${run.requestedBy ? ` by ${run.requestedBy}` : ''}`;
  return `${run.date} · daily${run.batch > 1 ? ` #${run.batch}` : ''}`;
}

function GenerateIdeasDialog({ open, ideation, onClose, onStarted }) {
  const [count, setCount] = useState('5');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const max = ideation?.maxIdeasPerRun || 20;
  const number = Number(count);
  const valid = Number.isInteger(number) && number >= 1 && number <= max;

  const start = async () => {
    try {
      setBusy(true);
      setError('');
      await VideoIdeaService.requestIdeas(number);
      onStarted();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start OpenCode.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Generate ideas</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          OpenCode writes new &quot;{ideation?.niche}&quot; ideas with thumbnails and adds them to your backlog.
          It usually takes about 10 minutes.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          margin="normal"
          type="number"
          label="Number of ideas"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          inputProps={{ min: 1, max }}
          error={!valid}
          helperText={valid ? ' ' : `Choose between 1 and ${max}.`}
          disabled={busy}
        />
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={start} disabled={busy || !valid}>
          {busy ? 'Starting…' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function IdeationPanel({ onIdeasAdded }) {
  const [ideation, setIdeation] = useState(null);
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      setIdeation(await VideoIdeaService.ideation());
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load OpenCode ideation.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // While a run is going, refresh the panel. When it finishes, reload the backlog.
  const activeRunIds = (ideation?.runs || [])
    .filter((run) => ACTIVE_RUN_STATUSES.includes(run.status))
    .map((run) => run.id)
    .join(',');
  useEffect(() => {
    if (!activeRunIds) return undefined;
    const timer = setInterval(load, REFRESH_WHILE_RUNNING_MS);
    return () => {
      clearInterval(timer);
      onIdeasAdded();
    };
  }, [activeRunIds, load, onIdeasAdded]);

  if (!ideation && !error) return null;
  const own = ideation && !ideation.seesAllAccounts ? ideation.accounts[0] : null;
  const stats = ideation?.seesAllAccounts ? ideation.totals : own || { dailyVideos: 0, backlog: 0, target: 0 };

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
          <AutoAwesomeIcon fontSize="small" /> OpenCode ideation
        </Typography>
        {ideation?.enabled && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            sx={{ whiteSpace: 'nowrap' }}
            onClick={() => setRequesting(true)}
            disabled={ideation.hasActiveRequest}
          >
            Generate ideas
          </Button>
        )}
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {ideation && !ideation.enabled && <Alert severity="warning">{ideation.problem}</Alert>}
      {ideation?.enabled && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Every day after midnight Eastern, OpenCode writes new &quot;{ideation.niche}&quot; ideas with thumbnails for
            each account whose automated channels need them, keeping {ideation.backlogDays} days of videos in each
            backlog. Each run makes up to {ideation.maxIdeasPerRun} ideas, and another starts when one finishes
            while backlogs still need more (up to {ideation.maxDailyRuns} runs a day).
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {ideation.seesAllAccounts ? 'All accounts' : 'Your account'}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mb: 1.5, mt: 0.5 }}>
            {[
              { label: 'Videos per day', value: stats.dailyVideos },
              { label: 'Backlog', value: stats.backlog },
              { label: 'Target', value: stats.target },
            ].map((stat) => (
              <Box key={stat.label} sx={{ p: 1, borderRadius: '10px', bgcolor: 'action.hover', textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{stat.value}</Typography>
                <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
              </Box>
            ))}
          </Box>
          {ideation.seesAllAccounts && ideation.accounts.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              {ideation.accounts.map((account) => (
                <Box
                  key={account.ownerId}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.5, borderTop: 1, borderColor: 'divider' }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{account.ownerName}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                    {account.dailyVideos}/day · {account.backlog} of {account.target} ideas
                    {account.ideasNeeded > 0 && ` · needs ${account.ideasNeeded}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <Typography variant="body2" sx={{ mb: 1.5 }}>{dailyDescription(ideation)}</Typography>
          {AuthService.hasRole(['superadmin']) && (
            <Box sx={{ mb: 1.5 }}>
              <Button
                component={RouterLink}
                to="/video-ideas/opencode-chat"
                variant="outlined"
                size="small"
                startIcon={<ForumIcon />}
              >
                Chat with OpenCode
              </Button>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Teach it better titles and thumbnails, then commit the chat for idea runs to use.
              </Typography>
            </Box>
          )}
        </>
      )}
      {ideation?.runs.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Recent runs</Typography>
          {ideation.runs.map((run) => {
            const status = RUN_STATUS[run.status] || RUN_STATUS.failed;
            return (
              <Box key={run.id} sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{runLabel(run)}</Typography>
                  <Chip size="small" color={status.color} label={status.label} />
                </Box>
                {run.ideasRequested !== null && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {run.ideasCreated === null
                      ? `${run.ideasRequested} ideas requested`
                      : `Added ${run.ideasCreated} of ${run.ideasRequested} ideas`}
                    {run.sessionVersion > 0 && ` · session v${run.sessionVersion}`}
                  </Typography>
                )}
                {(run.error || run.summary) && (
                  <Typography variant="caption" color={run.error ? 'error' : 'text.secondary'} component="div">
                    {run.error || run.summary}
                  </Typography>
                )}
              </Box>
            );
          })}
        </>
      )}
      <GenerateIdeasDialog
        open={requesting}
        ideation={ideation}
        onClose={() => setRequesting(false)}
        onStarted={() => {
          setRequesting(false);
          setNotice('OpenCode started. New ideas appear in your backlog when it finishes.');
          load();
        }}
      />
    </Paper>
  );
}

export default function VideoIdeasPage() {
  const [tab, setTab] = useState(0);
  const [niche, setNiche] = useState('');
  const [niches, setNiches] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [counts, setCounts] = useState({ queued: 0, used: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const status = TABS[tab].status;

  useEffect(() => {
    getPromptSystems().then((data) => setNiches(data.items || [])).catch(() => {});
  }, []);

  const load = useCallback(async (nextPage) => {
    try {
      setLoading(true);
      const data = await VideoIdeaService.list({ status, niche, page: nextPage, limit: PAGE_SIZE });
      setIdeas((current) => (nextPage === 1 ? data.items : [...current, ...data.items]));
      setCounts(data.counts);
      setTotal(data.total);
      setPage(nextPage);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load video ideas.');
    } finally {
      setLoading(false);
    }
  }, [status, niche]);

  useEffect(() => { load(1); }, [load]);
  const reloadIdeas = useCallback(() => load(1), [load]);

  const confirmDelete = async () => {
    try {
      await VideoIdeaService.remove(deleting.id);
      setDeleting(null);
      load(1);
    } catch (err) {
      setDeleting(null);
      setError(err.response?.data?.error || 'Could not delete the idea.');
    }
  };

  if (!AuthService.hasRole(['admin', 'superadmin'])) return <Navigate to="/" replace />;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 2, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
        Video Ideas
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 380px' }, gap: 2, alignItems: 'start' }}>
        <Paper sx={{ borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2, pt: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)}>
              {TABS.map((item) => (
                <Tab key={item.status} label={`${item.label} (${counts[item.status] ?? 0})`} />
              ))}
            </Tabs>
            <TextField
              select
              size="small"
              label="Niche"
              value={niche}
              onChange={(e) => setNiche(e.target.value)}
              sx={{ minWidth: 180, mb: 1 }}
            >
              <MenuItem value="">All niches</MenuItem>
              {niches.map((system) => (
                <MenuItem key={system.niche} value={system.niche}>{system.label || system.niche}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {status === 'queued' && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Automated channels use the oldest ideas first. Ideas reserved for one channel are used by that channel before shared ones.
              </Typography>
            )}
            {!loading && !ideas.length ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {status === 'queued'
                  ? 'The backlog is empty. Ideas added through the API appear here.'
                  : 'No ideas have been used yet.'}
              </Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(230px, 100%), 1fr))', gap: 2 }}>
                {ideas.map((idea) => <IdeaCard key={idea.id} idea={idea} onDelete={setDeleting} />)}
              </Box>
            )}
            {loading && (
              <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
            )}
            {!loading && ideas.length < total && (
              <Stack alignItems="center" sx={{ mt: 2 }}>
                <Button onClick={() => load(page + 1)}>Load more</Button>
              </Stack>
            )}
          </Box>
        </Paper>
        <Stack spacing={2}>
          <IdeationPanel onIdeasAdded={reloadIdeas} />
          <ApiAccessPanel />
        </Stack>
      </Box>

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)} maxWidth="xs">
        <DialogTitle>Delete idea</DialogTitle>
        <DialogContent>
          <Typography>Remove &quot;{deleting?.title}&quot; from the backlog?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
